import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Layers } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";
import { VerifyChip } from "../components/DataLabel";
import { SiteLayoutPlanner } from "../components/SiteLayoutPlanner";
import { CountyComparison } from "../components/CountyComparison";
import { FacilityPicker } from "../components/FacilityPicker";
import { StartAgain } from "../components/StartAgain";

const ksh = (n) => `KSh ${Math.round(n).toLocaleString()}`;
const usd = (n) => `$${Math.round(n).toLocaleString()}`;

const Card = ({ label, value, note, tone = "text-white", testId }) => (
  <div className="border border-zinc-900 bg-[#0b0b0b] p-5" data-testid={testId}>
    <p className="mono text-[0.5rem] uppercase tracking-[0.18em] text-zinc-500">{label}</p>
    <p className={`mt-3 font-display text-2xl font-bold leading-none md:text-3xl ${tone}`}>{value}</p>
    {note ? <p className="mt-2 text-xs leading-relaxed text-zinc-500">{note}</p> : null}
  </div>
);

const Lines = ({ title, lines, unit, total, testId }) => (
  <div data-testid={testId}>
    <p className="mono text-[0.55rem] uppercase tracking-[0.18em] text-ochre">{title}</p>
    <div className="mt-4 border-t border-white/15">
      {lines.map((l) => (
        <div key={l.line} className="flex items-baseline justify-between gap-6 border-b border-white/8 py-3">
          <span className="text-sm leading-snug text-zinc-300">{l.line}</span>
          <span className="mono whitespace-nowrap text-xs text-white">
            {unit === "usd" ? usd(l.usd) : ksh(l.ksh)}
          </span>
        </div>
      ))}
      <div className="flex items-baseline justify-between gap-6 py-3">
        <span className="mono text-[0.55rem] uppercase tracking-[0.16em] text-zinc-400">Total</span>
        <span className="mono whitespace-nowrap text-sm text-ochre">{unit === "usd" ? usd(total) : ksh(total)}</span>
      </div>
    </div>
  </div>
);

/** Choose a facility, a county and a funding route, and see whether it stands up. */
export default function FacilityPlanner() {
  const [params, setParams] = useSearchParams();
  const [lib, setLib] = useState(null);
  const [counties, setCounties] = useState([]);
  const facility = params.get("facility") || "community_court";
  const county = params.get("county") || "Kisumu";
  const funding = params.get("funding") || "ppp";
  const tier = params.get("tier") || "";
  const [data, setData] = useState(null);

  const set = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  const setFacility = (v) => set("facility", v);
  const setCounty = (v) => set("county", v);
  const setFunding = (v) => set("funding", v);
  const setTier = (v) => set("tier", v);

  useSeo({
    title: "Facility Planner — Capex, Opex And Operating Surplus By County",
    description:
      "Nine facility types from a community court to a Premier League standard ground, including stacked decks for cities above a million: what each serves, who funds it, how much land it needs, and what it costs and earns in any of the 47 counties.",
  });

  useEffect(() => {
    api.get("/facility-library").then((r) => setLib(r.data)).catch(() => setLib(null));
    api.get("/counties")
      .then((r) => setCounties((Array.isArray(r.data) ? r.data : r.data.counties || []).map((c) => c.name).filter(Boolean)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/facility-model/${facility}?county=${encodeURIComponent(county)}&funding=${funding}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [facility, county, funding]);

  const shown = useMemo(
    () => (lib ? lib.facilities.filter((f) => !tier || f.tier === tier) : []),
    [lib, tier],
  );

  const chosen = lib?.facilities.find((f) => f.id === facility);
  const fundingOptions = chosen ? lib.funding.filter((f) => chosen.funding.includes(f.id)) : [];

  useEffect(() => {
    if (shown.length && !shown.some((f) => f.id === facility)) setFacility(shown[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown, facility]);

  useEffect(() => {
    if (chosen && !chosen.funding.includes(funding)) setFunding(chosen.funding[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosen, funding]);

  const select = "mt-2 w-full border border-white/20 bg-black px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none";
  const label = "mono block text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500";
  const trading = data?.commercial?.test === "trading";

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="facility-planner-page">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <Reveal>
          <p className="overline text-ochre">Facilities, funding and running costs</p>
          <h1 className="display-1 mt-5 max-w-4xl">What To Build, Who It Serves, What It Earns</h1>
          <p className="body-copy mt-6 max-w-3xl">
            Nine facility types, from one community court to a Premier League standard ground, including stacked
            decks for
            cities above a million people. Pick a county and a funding route and the model prices it off that
            county&rsquo;s own living wage, rent, water and electricity tariffs and its youth population — not a
            London price list.
          </p>
          <p className="mono mt-6 flex max-w-3xl items-start gap-3 border-l-2 border-maasai bg-[#0b0b0b] p-5 text-[0.6rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-400">
            <Layers size={16} className="mt-0.5 shrink-0 text-maasai" />
            <span>
              <VerifyChip v="model" /> Modelled from Powerleague&rsquo;s published operating economics and
              re-priced for Kenya. Estimates to be argued with, not a bankable model.
            </span>
          </p>
        </Reveal>

        {lib ? (
          <>
            <section className="mt-14" data-testid="facility-controls">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <p className="eyebrow">What you would build</p>
                  <h2 className="display-3 mt-3">Pick The Facility</h2>
                </div>
                <StartAgain label="Start again" to="/facility-planner" testId="planner-start-again" />
              </div>
              <div className="mt-7 grid gap-5 sm:grid-cols-3">
                <label className="block">
                  <span className={label}>Level of the game</span>
                  <select value={tier} onChange={(e) => setTier(e.target.value)} data-testid="tier-select" className={select}>
                    <option value="">All levels</option>
                    {lib.tiers.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className={label}>County</span>
                  <select value={county} onChange={(e) => setCounty(e.target.value)} data-testid="county-select" className={select}>
                    {counties.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className={label}>Funding route</span>
                  <select value={funding} onChange={(e) => setFunding(e.target.value)} data-testid="funding-select" className={select}>
                    {fundingOptions.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                  </select>
                </label>
              </div>

              <p className="mono mt-8 text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">
                {shown.length} {shown.length === 1 ? "facility" : "facilities"}
                {tier ? ` at ${lib.tiers.find((t) => t.id === tier)?.label.toLowerCase()} level` : " across every level"}
                {" "}· tap one to price it
              </p>
              <div className="mt-4">
                <FacilityPicker facilities={shown} value={facility} onChange={setFacility} testId="planner-facility-picker" />
              </div>
              <label className="mt-6 block max-w-xs">
                <span className={label}>Or choose from a list</span>
                <select value={facility} onChange={(e) => setFacility(e.target.value)} data-testid="facility-select" className={select}>
                  {shown.map((f) => <option key={f.id} value={f.id}>{`${f.name} — ${f.tier_label}`}</option>)}
                </select>
              </label>
            </section>

            {data ? (
              <>
                <section className="mt-12" data-testid="facility-summary">
                  <h2 className="display-2 max-w-3xl">{data.facility.name}</h2>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="mono border border-ochre/50 px-2.5 py-1 text-[0.5rem] uppercase tracking-[0.16em] text-ochre" data-testid="facility-tier-tag">
                      {data.facility.tier_label}
                    </span>
                    <span className="mono border border-white/25 px-2.5 py-1 text-[0.5rem] uppercase tracking-[0.16em] text-white" data-testid="facility-funding-tag">
                      {data.funding.label}
                    </span>
                    <span className="mono border border-zinc-700 px-2.5 py-1 text-[0.5rem] uppercase tracking-[0.16em] text-zinc-400">
                      {chosen?.pitch_summary}
                    </span>
                  </div>
                  <p className="body-copy mt-6 max-w-3xl">{data.facility.purpose}</p>
                  <p className="mt-4 max-w-3xl text-sm leading-relaxed text-zinc-400">
                    <span className="text-white">Used for: </span>{data.facility.used_for}
                  </p>
                  <p className="mt-4 max-w-3xl text-sm leading-relaxed text-zinc-400">
                    <span className="text-white">{data.facility.tier_label}: </span>{data.facility.tier_note}
                  </p>
                  <p className="mt-4 max-w-3xl text-sm leading-relaxed text-zinc-400">
                    <span className="text-white">{data.funding.label} funding: </span>{data.funding.note}
                  </p>
                {data.eligibility?.note ? (
                  <p className="mono mt-6 max-w-3xl border-l-2 border-zinc-700 bg-[#0b0b0b] p-4 text-[0.58rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-400" data-testid="facility-eligibility">
                    {data.eligibility.eligible ? "" : "Not recommended here — "}
                    {data.eligibility.note}
                    {data.land.levels > 1 ? ` Saves ${data.land.land_saved_m2.toLocaleString()} m² of land against building flat.` : ""}
                  </p>
                ) : null}
                </section>

                <section className="mt-12 grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="facility-results">
                  <Card label="Build cost" value={usd(data.result.capex_usd)} note={`${ksh(data.capex.total_ksh)} · cost index ${data.cost_index}x`} testId="result-capex" />
                  <Card label="Net trading income, year" value={ksh(data.result.net_revenue_ksh)} note={`Peak hire ${ksh(data.revenue.hire_rate_peak_ksh)} an hour · demand index ${data.revenue.demand_index}`} testId="result-revenue" />
                  <Card
                    label={trading ? "EBITDA, year" : data.result.annual_gap_ksh ? "Annual funding gap" : "Trading surplus before football income"}
                    value={trading ? ksh(data.result.ebitda_ksh) : ksh(data.result.annual_gap_ksh || data.result.ebitda_ksh)}
                    note={trading ? `${data.result.ebitda_margin_pct}% margin` : data.result.annual_gap_ksh ? "To be met by gate, sponsorship, league income or a county" : "Before gate, sponsorship and league income, which sit outside this model"}
                    tone={trading || !data.result.annual_gap_ksh ? "text-ochre" : "text-maasai"}
                    testId="result-ebitda"
                  />
                  <Card
                    label={trading ? "Years to match the build cost" : "Land needed"}
                    value={trading ? (data.result.payback_years ? `${data.result.payback_years} yrs` : "n/a") : `${data.land.land_acres} ac`}
                    note={trading ? (data.result.roce_pct ? `${data.result.roce_pct}% operating surplus, share of build cost` : "Grant funded — judged on participation") : `${data.land.land_m2.toLocaleString()} m²`}
                    testId="result-return"
                  />
                </section>

                <p className="mt-6 max-w-4xl border-l-2 border-ochre bg-[#0b0b0b] p-5 text-sm leading-relaxed text-zinc-300" data-testid="commercial-test">
                  {data.commercial.note}
                </p>

                <section className="mt-14 grid gap-12 lg:grid-cols-3" data-testid="facility-breakdown">
                  <Lines title="Capital cost" lines={data.capex.lines} unit="usd" total={data.capex.total_usd} testId="capex-lines" />
                  <Lines title="Trading income, year one" lines={data.revenue.streams.map((s) => ({ line: s.line, ksh: s.ksh }))} unit="ksh" total={data.revenue.gross_ksh} testId="revenue-lines" />
                  <Lines title="Running cost, year" lines={data.opex.lines} unit="ksh" total={data.opex.total_ksh} testId="opex-lines" />
                </section>

                <section className="mt-14 grid gap-8 lg:grid-cols-2">
                  <div className="border border-zinc-900 bg-[#0b0b0b] p-6" data-testid="county-context">
                    <p className="mono text-[0.55rem] uppercase tracking-[0.18em] text-ochre">{county} in this model</p>
                    <dl className="mono mt-5 grid grid-cols-2 gap-5 text-xs">
                      {[
                        ["Cost index", `${data.cost_index}x`],
                        ["Aged 15 to 34", data.youth_15_34.toLocaleString()],
                        ["School age (5-19)", data.school_age.toLocaleString()],
                        ["Land needed", `${data.land.land_m2.toLocaleString()} m² (${data.land.land_acres} ac)`],
                      ].map(([k, v]) => (
                        <div key={k}>
                          <dt className="text-[0.5rem] uppercase tracking-[0.18em] text-zinc-600">{k}</dt>
                          <dd className="mt-1 text-white">{v}</dd>
                        </div>
                      ))}
                    </dl>
                    <p className="mt-5 text-xs leading-relaxed text-zinc-500">{data.revenue.demand_note}</p>
                  </div>
                  <div className="border border-zinc-900 bg-[#0b0b0b] p-6" data-testid="operator-benchmark">
                    <p className="mono text-[0.55rem] uppercase tracking-[0.18em] text-ochre">Benchmark: {data.benchmark.operator}</p>
                    <ul className="mt-5 space-y-3">
                      {data.benchmark.lines.map((l) => (
                        <li key={l} className="border-l-2 border-zinc-800 pl-4 text-xs leading-relaxed text-zinc-300">{l}</li>
                      ))}
                    </ul>
                    <p className="mt-5 text-xs leading-relaxed text-zinc-400">{data.benchmark.how_we_differ}</p>
                  </div>
                </section>

                <section className="mt-14" data-testid="facility-assumptions">
                  <p className="eyebrow">How the model works</p>
                  <ul className="mt-6 max-w-4xl space-y-4">
                    {data.assumptions.map((a) => (
                      <li key={a} className="border-l-2 border-zinc-800 pl-5 text-sm leading-relaxed text-zinc-300">{a}</li>
                    ))}
                  </ul>
                  <p className="mono mt-6 max-w-3xl text-[0.55rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-500">
                    {lib.league_note}
                  </p>
                </section>
              </>
            ) : null}

            <CountyComparison facility={facility} funding={funding} />

            <SiteLayoutPlanner />

            <div className="mt-14 flex flex-wrap gap-3">
              <Link to="/apply" className="btn-primary" data-testid="planner-apply-cta">Tell us about a site like this</Link>
              <Link to={`/compare-facilities?a=${facility}&county=${county}&funding=${funding}`} className="btn-ghost" data-testid="planner-compare-cta">
                Compare with another facility
              </Link>
              <Link to="/pitch-sizing" className="btn-ghost">Pitch sizing and land</Link>
              <Link to="/court-rollout" className="btn-ghost">The county court rollout</Link>
              <Link to="/sources" className="btn-ghost">Sources and method</Link>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
